import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { VDropdown } from '@client/components/_shared/forms/view-form/VDropdown';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { ICONS } from '@client/constants/icons';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { PRODUCT_TYPE_CATEGORY_LABELS } from '@shared/constants';
import type { ProductTypeCategory } from '@shared/prisma/enums';
import { createFileRoute } from '@tanstack/react-router';
import { Divider, Skeleton } from 'antd';
import { productTypeFormStore } from './store';

const PRODUCT_TYPE_CATEGORY_OPTIONS = (Object.keys(PRODUCT_TYPE_CATEGORY_LABELS) as ProductTypeCategory[]).map(
    (value) => ({ value, label: PRODUCT_TYPE_CATEGORY_LABELS[value] }),
);

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/product-types/edit/$productTypeId')(
    {
        staticData: {
            presentation: 'overlay',
            metadata: {
                title: 'Edit product type',
            },
        },
        params: {
            parse({ productTypeId }) {
                return {
                    productTypeId: BigInt(productTypeId),
                };
            },
        },
        component: RouteComponent,
    },
);

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProductTypeEdit />
        </PageController>
    );
}

function ProductTypeEdit() {
    const { trpcUtils } = useTrpc();
    const { productTypeId } = Route.useParams();
    const { data, isPending, error, isFetching, refetch } = trpc.productType.getProductType.useQuery({
        id: productTypeId,
    });
    const { mutate } = trpc.productType.updateProductType.useMutation();
    const navigate = useNavigate();

    const { formData } = productTypeFormStore.useInit({
        entityLabel: 'Product type',
        data,
        isLoading: isPending,

        async onReload() {
            await refetch();
        },

        onSave(_, changedValues) {
            return new Promise((resolve) => {
                message.loading('Saving...');

                mutate(
                    {
                        id: productTypeId,
                        set: {
                            ...changedValues,
                            definition: changedValues.definition === null ? undefined : changedValues.definition,
                            thirdPartyContactTypes:
                                changedValues.thirdPartyContactTypes === null
                                    ? []
                                    : changedValues.thirdPartyContactTypes,
                        },
                    },
                    {
                        onSuccess() {
                            message.success('Saved.');
                            resolve({ success: true });
                        },
                        onError(error) {
                            if (error.data?.errorId === 'productType.alreadyExists') {
                                message.error('This name is already in use.');
                                resolve({ success: true });
                                return;
                            }

                            resolve({ success: false, error });
                        },
                    },
                );
            });
        },
    });

    return (
        <OverlayPage
            size="large"
            title="Edit product type"
            // The fields save one at a time; only a field still open for editing holds input to lose.
            isDirty={() => productTypeFormStore.getState().editingField !== undefined}
            afterClose={() => {
                void trpcUtils.productType.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ requestClose }) => (
                <Button type="secondary" onClick={requestClose}>
                    Cancel
                </Button>
            )}
        >
            {error ? (
                <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />
            ) : (
                <>
                    <Divider titlePlacement="left" className="mt-0 mb-4 text-sm text-subtle-foreground">
                        General
                    </Divider>

                    <div className="mb-4 grid grid-cols-4 gap-4">
                        <VTextInput
                            label="Name"
                            field="name"
                            rules={[{ required: true }]}
                            formStore={productTypeFormStore}
                        />
                        <VDropdown
                            label="Category"
                            field="category"
                            options={PRODUCT_TYPE_CATEGORY_OPTIONS}
                            formStore={productTypeFormStore}
                        />
                    </div>

                    <Divider className="my-4" />

                    {formData?.id === productTypeId ? (
                        <Tabs
                            items={[
                                {
                                    to: '/console/$orgId/configuration/claims-products/product-types/edit/$productTypeId/third-party-contact-type',
                                    icon: <ICONS.contacts className="size-4" />,
                                    label: 'Third party contact types',
                                },
                                {
                                    to: '/console/$orgId/configuration/claims-products/product-types/edit/$productTypeId/data-fields',
                                    icon: <ICONS.productType className="size-4" />,
                                    label: 'Product type custom fields',
                                },
                                {
                                    to: '/console/$orgId/configuration/claims-products/product-types/edit/$productTypeId/policy-fields',
                                    icon: <ICONS.policyInfo className="size-4" />,
                                    label: 'Policy tab custom fields',
                                },
                            ]}
                        />
                    ) : (
                        <Skeleton active />
                    )}
                </>
            )}
        </OverlayPage>
    );
}
